import { kebabCase, memoize } from 'lodash-es';
import type { KebabCasedProperties } from 'type-fest';
import { computed, type ComputedRef } from 'vue';

const toKebab = memoize(kebabCase);

type Options = {
  exclude?: readonly string[];
  kebabCase?: boolean;
};

type UseWebComponentProps = {
  <T extends Record<string, unknown>, E extends readonly string[]>(
    props: T,
    options: { exclude: E; kebabCase: false }
  ): ComputedRef<Omit<T, E[number]>>;

  <T extends Record<string, unknown>>(props: T, options: { kebabCase: false }): ComputedRef<T>;

  <T extends Record<string, unknown>, E extends readonly string[]>(
    props: T,
    options: { exclude: E; kebabCase?: true }
  ): ComputedRef<KebabCasedProperties<Omit<T, E[number]>>>;

  <T extends Record<string, unknown>>(props: T, options: { kebabCase?: true }): ComputedRef<KebabCasedProperties<T>>;

  <T extends Record<string, unknown>>(props: T): ComputedRef<KebabCasedProperties<T>>;
};

export const useWebComponentProps: UseWebComponentProps = (props: Record<string, unknown>, options: Options = {}) => {
  const { exclude = [], kebabCase: useKebabCase = true } = options;

  return computed(() => {
    const res: Record<string, unknown> = {};

    for (const key in props) {
      if (!exclude.includes(key)) {
        res[useKebabCase ? toKebab(key) : key] = props[key];
      }
    }

    return res;
  });
};
